import { useMutation } from '@apollo/client';
import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types';
import type { UpdateRiskAssessmentResultConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetLatestRiskAssessmentResultConfigDocument,
  UpdateRiskAssessmentResultConfigDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useUpdateRiskAssessmentResultConfigTRPC } from './useUpdateRiskAssessmentResultConfigTRPC';

export const useUpdateRiskAssessmentResultConfig = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [mutateGraphQL, graphqlState] = useMutation(
    UpdateRiskAssessmentResultConfigDocument,
    {
      refetchQueries: [
        {
          query: GetLatestRiskAssessmentResultConfigDocument,
          variables: { Type: 'risk_assessment' },
        },
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateRiskAssessmentResultConfigTRPC();

  const mutate = async (variables: {
    variables: {
      Id: string;
      Config: RiskAssessmentResultConfig;
      OriginalTimestamp: string;
    };
  }): Promise<UpdateRiskAssessmentResultConfigMutation> => {
    if (trpcEnabled) {
      return trpcMutation.mutate(variables);
    }

    const result = await mutateGraphQL(variables);
    if (!result.data) {
      throw new Error('Failed to update risk assessment result config');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      mutate,
      loading: trpcMutation.loading,
    };
  }

  return {
    mutate,
    loading: graphqlState.loading,
  };
};
